import "zod/compile";
import { z } from "zod";

/** Legacy note metadata; accepted for compatibility but no longer controls lookup. */
export const remittanceMemoryCandidateScopeSchema = z.enum([
  "payer",
  "organization",
]);
export type RemittanceMemoryCandidateScope = z.infer<
  typeof remittanceMemoryCandidateScopeSchema
>;

export const REMITTANCE_MEMORY_CANDIDATE_SCOPE_METADATA_KEY =
  "remittanceCandidateScope";

export const remittanceMemoryCitationSchema = z.object({
  noteId: z.uuid(),
  excerpt: z.string().min(1).max(4000),
});

/**
 * One piece of a reference template. Code, not the model, turns segments into
 * a matcher, so no model-generated regex ever runs.
 * - `literal`: exactly one of `literals`, case-sensitive
 * - `digits` / `letters` / `alphanumeric`: one or more ASCII characters of that class
 * - `any`: zero or more of any character
 */
export const remittanceReferenceSegmentSchema = z.object({
  match: z.enum(["literal", "digits", "letters", "alphanumeric", "any"]),
  /**
   * Required for `literal`, null otherwise. More than one entry when the note
   * lists alternatives ("INV- or CRM-").
   */
  literals: z.array(z.string().min(1).max(100)).min(1).max(10).nullable(),
  keep: z.boolean(),
});

/** A before-and-after pair that must appear in the cited note. */
export const remittanceReferenceExampleSchema = z.object({
  reference: z.string().min(1).max(100),
  result: z.string().min(1).max(100),
});

/**
 * A whole reference is matched against the segments in order; the kept
 * segments, joined, become the identifier to look up. The rule is trusted only
 * when running it on every cited example reproduces that example's result.
 */
export const remittanceReferenceRuleSchema = z.object({
  operation: z.literal("template"),
  target: z.enum(["invoice_number", "customer_po"]),
  segments: z.array(remittanceReferenceSegmentSchema).min(1).max(8),
  examples: z.array(remittanceReferenceExampleSchema).min(1).max(10),
  citations: z.array(remittanceMemoryCitationSchema).min(1).max(20),
  explanation: z.string().min(1).max(1000),
});

export const remittanceMemoryInterpretationSchema = z.object({
  outcome: z.enum(["interpreted", "unsupported", "conflict"]),
  rules: z.array(remittanceReferenceRuleSchema).max(40),
  explanation: z.string().min(1).max(2000),
});

/**
 * What customer memory did during one resolution pass. `failed` and
 * `limit_exceeded` are produced by code, not the interpreter, and mean the
 * notes were never applied.
 */
export const remittanceMemorySummarySchema = z.object({
  outcome: z.enum([
    "interpreted",
    "unsupported",
    "conflict",
    "failed",
    "limit_exceeded",
  ]),
  explanation: z.string(),
  noteCount: z.number().int().nonnegative(),
  ruleCount: z.number().int().nonnegative(),
});

export const remittanceMemoryNoteEvidenceSchema = z.object({
  noteId: z.uuid(),
  versionId: z.uuid().nullable(),
  version: z.int().positive().nullable(),
  contentHash: z.string(),
});

export type RemittanceReferenceSegment = z.infer<
  typeof remittanceReferenceSegmentSchema
>;
export type RemittanceReferenceRule = z.infer<
  typeof remittanceReferenceRuleSchema
>;
export type RemittanceMemoryInterpretation = z.infer<
  typeof remittanceMemoryInterpretationSchema
>;
export type RemittanceMemorySummary = z.infer<
  typeof remittanceMemorySummarySchema
>;
export type RemittanceMemoryNoteEvidence = z.infer<
  typeof remittanceMemoryNoteEvidenceSchema
>;
export type RemittanceMemoryNote = RemittanceMemoryNoteEvidence & {
  content: string;
};

export const remittanceInvoiceCandidateSchema = z.object({
  id: z.uuid(),
  invoiceNumber: z.string().nullable(),
  customerId: z.uuid().nullable(),
  customerName: z.string().nullable(),
});
export type RemittanceInvoiceCandidate = z.infer<
  typeof remittanceInvoiceCandidateSchema
>;

export const remittanceReprocessCountsSchema = z.object({
  matched: z.number().int().nonnegative(),
  ambiguous: z.number().int().nonnegative(),
  unmatched: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type RemittanceReprocessCounts = z.infer<
  typeof remittanceReprocessCountsSchema
>;
